import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Crosshair, Zap, AlertTriangle, AlertOctagon, Quote, CheckCircle2,
  Loader2, Send, Copy,
} from "lucide-react";
import { getPracticeMode, subscribePractice } from "@/lib/practice";
import PracticeSuccessModal from "@/components/PracticeSuccessModal";

/**
 * Lead Hunter — AI Salesperson.
 * Leader pastes a prospect URL / email → Claude returns a 3-point Leak
 * Briefing + a Grid Pitch. One click flips the pitch `gvo_ready:true`
 * for the Auto-Net outreach pipeline.
 */
export default function LeadHunter({ forcePractice = false } = {}) {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hunt, setHunt] = useState(null);
  const [approving, setApproving] = useState(false);
  const [practiceState, setPracticeState] = useState(getPracticeMode);
  const [practiceModal, setPracticeModal] = useState(false);
  const practice = forcePractice || practiceState;

  useEffect(() => subscribePractice(setPracticeState), []);

  const runHunt = async (e) => {
    e?.preventDefault?.();
    const raw = input.trim();
    if (!raw) {
      toast.error("Paste a prospect URL or email first.");
      return;
    }
    setLoading(true);
    setHunt(null);
    try {
      const { data } = await api.post("/lead-hunter/hunt", { url: raw, practice });
      setHunt(data.hunt);
      if (data.hunt?.source === "fallback") {
        toast.warning("Grid AI unavailable — using deterministic briefing.");
      } else {
        toast.success(practice ? "Simulator audit complete." : "Prospect audited. Pitch ready to review.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Hunt failed.");
    } finally {
      setLoading(false);
    }
  };

  const approve = async () => {
    if (!hunt?.id) return;
    // Practice Mode: no real API call — celebrate + mark local state.
    if (practice) {
      setPracticeModal(true);
      setHunt((h) => (h ? { ...h, gvo_ready: true, approved_at: new Date().toISOString(), practice: true } : h));
      return;
    }
    setApproving(true);
    try {
      const { data } = await api.post(`/lead-hunter/${hunt.id}/approve`);
      setHunt(data.hunt);
      toast.success("Pitch queued for GVO Auto-Net.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed.");
    } finally {
      setApproving(false);
    }
  };

  const copyPitch = async () => {
    if (!hunt?.pitch) return;
    try {
      await navigator.clipboard.writeText(hunt.pitch);
      toast.success("Pitch copied.");
    } catch {
      toast.error("Clipboard unavailable.");
    }
  };

  return (
    <section
      className="glass-strong rounded-sm overflow-hidden"
      data-testid="lead-hunter-panel"
    >
      {/* Header strip */}
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-8 h-8 rounded-sm flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, rgba(212,175,55,0.25), rgba(16,185,129,0.18))",
              border: "1px solid rgba(212,175,55,0.5)",
            }}
          >
            <Crosshair size={16} className="text-gold" />
          </div>
          <div className="min-w-0">
            <div className="hud-label text-[9px]">AI Salesperson · Live</div>
            <div className="font-display text-white text-lg leading-tight">
              Lead Hunter <span className="text-gold italic">Command</span>
            </div>
          </div>
        </div>
        <span className="hidden sm:inline text-[10px] uppercase tracking-[0.28em] text-emerald-300/80">
          {practice ? "Simulator · Practice" : "Grid · Auto-Net Ready"}
        </span>
      </div>

      {/* Input row */}
      <form onSubmit={runHunt} className="p-5 sm:p-6 border-b border-white/5">
        <label className="hud-label text-[10px] block mb-2" htmlFor="lead-hunter-input">
          Prospect URL or Email
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            id="lead-hunter-input"
            data-testid="lead-hunter-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading}
            placeholder="https://acmecoffee.com  or  owner@acmecoffee.com"
            className="flex-1 bg-black/40 border border-white/10 focus:border-gold/70 outline-none px-4 py-3 rounded-sm text-white text-sm placeholder:text-slate-600 font-mono-aviation transition-colors"
            autoComplete="off"
            spellCheck="false"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            data-testid="lead-hunter-hunt-button"
            className="btn-gold px-6 py-3 rounded-sm text-xs sm:text-sm uppercase tracking-[0.24em] flex items-center justify-center gap-2 min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Hunting…
              </>
            ) : (
              <>
                <Zap size={14} />
                Hunt
              </>
            )}
          </button>
        </div>
        <p className="text-[10px] text-slate-500 mt-3 leading-relaxed">
          Alfred crawls the prospect, isolates 3 revenue leaks, and drafts a
          personalised outreach pitch you can approve into the GVO Auto-Net.
        </p>
      </form>

      {/* Loading skeleton */}
      {loading && !hunt && (
        <div className="p-6 space-y-4" data-testid="lead-hunter-loading">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => (
              <div
                key={`sk-${i}`}
                className="h-24 rounded-sm border border-white/5 bg-white/[0.02] animate-pulse"
                style={{ animationDelay: `${i * 120}ms` }}
              />
            ))}
          </div>
          <div className="h-32 rounded-sm border border-white/5 bg-white/[0.02] animate-pulse" />
        </div>
      )}

      {/* Result */}
      {hunt && !loading && (
        <div className="p-5 sm:p-6 space-y-6" data-testid="lead-hunter-result">
          {/* Business + meta */}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <div className="hud-label text-[10px]">Audited</div>
            <div className="font-display text-white text-xl truncate">
              {hunt.business}
            </div>
            <span className="text-[11px] text-slate-500 font-mono-aviation truncate">
              {hunt.url}
            </span>
            {hunt.gvo_ready && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.24em] px-2 py-0.5 rounded-sm border border-emerald-400/40 text-emerald-300"
                data-testid="gvo-ready-badge"
              >
                <CheckCircle2 size={10} />
                GVO Queued
              </span>
            )}
          </div>

          {/* Leak Briefing */}
          <div>
            <div className="hud-label text-[10px] mb-3 flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: "#F43F5E" }}
              />
              Leak Briefing · Where the revenue walks out
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="leak-briefing">
              {hunt.briefing.map((leak, idx) => (
                <LeakCard key={leak.title + idx} leak={leak} />
              ))}
            </div>
          </div>

          {/* The Grid Pitch */}
          <div>
            <div className="hud-label text-[10px] mb-3 flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: "#D4AF37" }}
              />
              The Grid Pitch · Ready for John / Parker
            </div>
            <div
              className="relative rounded-sm border p-5 sm:p-6"
              style={{
                borderColor: "rgba(212,175,55,0.35)",
                background:
                  "linear-gradient(135deg, rgba(6,11,20,0.85) 0%, rgba(16,185,129,0.08) 60%, rgba(212,175,55,0.10) 100%)",
              }}
              data-testid="grid-pitch"
            >
              <Quote
                size={22}
                className="text-gold/60 absolute top-3 left-3"
                aria-hidden
              />
              <p className="text-white text-sm sm:text-[15px] leading-relaxed pl-8 whitespace-pre-wrap">
                {hunt.pitch}
              </p>
              <div className="mt-4 pl-8 text-[10px] uppercase tracking-[0.28em] text-slate-500">
                Source: {hunt.source === "fallback" ? "Deterministic template" : "Claude Sonnet 4.6"}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-white/5">
            <button
              onClick={approve}
              disabled={approving || hunt.gvo_ready}
              data-testid="lead-hunter-approve-button"
              className="btn-gold px-5 py-3 rounded-sm text-xs uppercase tracking-[0.22em] flex items-center justify-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed flex-1"
            >
              {approving ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Queuing…
                </>
              ) : hunt.gvo_ready ? (
                <>
                  <CheckCircle2 size={14} />
                  {practice ? "Simulator Queue Cleared" : "Queued in Auto-Net"}
                </>
              ) : practice ? (
                <>
                  <Send size={14} /> Simulate Approval &amp; Queue
                </>
              ) : (
                <>
                  <Send size={14} /> Approve &amp; Inject to GVO Auto-Net
                </>
              )}
            </button>
            <button
              onClick={copyPitch}
              data-testid="lead-hunter-copy-button"
              className="btn-ghost-gold px-5 py-3 rounded-sm text-xs uppercase tracking-[0.22em] flex items-center justify-center gap-2 min-h-[44px]"
            >
              <Copy size={14} /> Copy Pitch
            </button>
          </div>
        </div>
      )}
      <PracticeSuccessModal
        open={practiceModal}
        businessName={hunt?.business}
        onClose={() => setPracticeModal(false)}
      />
    </section>
  );
}

function LeakCard({ leak }) {
  const isRed = leak.severity === "red";
  const color = isRed ? "#F43F5E" : "#FBBF24";
  const Icon = isRed ? AlertOctagon : AlertTriangle;
  return (
    <div
      className="rounded-sm border p-4 hover:bg-white/[0.02] transition-colors h-full"
      style={{
        borderColor: isRed ? "rgba(244,63,94,0.35)" : "rgba(251,191,36,0.35)",
        background:
          "linear-gradient(160deg, rgba(6,11,20,0.6), rgba(6,11,20,0.35))",
      }}
      data-testid={`leak-card-${leak.severity}`}
    >
      <div className="flex items-start gap-2 mb-2">
        <Icon size={14} style={{ color }} className="mt-0.5 shrink-0" />
        <div
          className="text-[9px] uppercase tracking-[0.28em] font-mono-aviation"
          style={{ color }}
        >
          {leak.severity === "red" ? "Active Bleed" : "Weakness"}
        </div>
      </div>
      <div className="text-white font-display text-sm leading-snug mb-2">
        {leak.title}
      </div>
      <div className="text-[12px] text-slate-400 leading-relaxed">
        {leak.detail}
      </div>
    </div>
  );
}
